// src/hooks/useSessionTracker.js
//
// Tracks an authenticated user's dashboard session:
//
//   1. On mount, generates a UUID, POSTs /api/sessions/start with auth.
//   2. While the tab is *visible*, POSTs /api/sessions/heartbeat every 30s.
//      When the tab is hidden (background tab, minimized), the timer pauses
//      so we don't count tab-open-but-not-looking time as engagement.
//   3. On `pagehide` / `beforeunload`, fires a sendBeacon to /api/sessions/end
//      with reason='unload' — survives the tab being closed.
//   4. On explicit React unmount (e.g. user navigates away), POSTs
//      /api/sessions/end with reason='navigation'.
//
// The hook returns:
//   - sessionId          (string)  the active session UUID
//   - logEvent(type, payload)      record an interaction event (auth POST)
//   - endSession(reason)           manually end the session (used by logout)
//
// All work is best-effort: a failed start/heartbeat/event won't crash the UI,
// it just means the admin view won't see that data point. We surface warnings
// in dev only.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiPost, sendBeaconJSON, ApiError } from '../api';

const HEARTBEAT_MS = 30_000;
const DEV = process.env.NODE_ENV !== 'production';

function newSessionId() {
  // crypto.randomUUID() is available in all modern browsers; fall back to
  // a Math.random-based UUID v4 if not.
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function useSessionTracker(user) {
  // We use a ref for the session id so the unload handler (registered once)
  // always sees the latest value without re-binding.
  const sessionIdRef = useRef(null);
  const startedRef = useRef(false);
  const endedRef = useRef(false);
  const [sessionId, setSessionId] = useState(null);

  // ---------------- start ----------------
  useEffect(() => {
    if (!user || startedRef.current) return;

    const sid = newSessionId();
    sessionIdRef.current = sid;
    setSessionId(sid);
    startedRef.current = true;

    apiPost('/api/sessions/start', { session_id: sid }, user).catch((err) => {
      if (DEV) console.warn('[session] start failed:', err);
      // We still keep the session id locally; subsequent heartbeats will 404
      // and that's fine — better than blocking the UI.
    });

    // ---------------- end on unmount ----------------
    return () => {
      if (endedRef.current) return;
      endedRef.current = true;
      sendBeaconJSON('/api/sessions/end', {
        session_id: sid,
        reason: 'navigation',
      });
    };
  }, [user]);

  // ---------------- heartbeat loop ----------------
  useEffect(() => {
    if (!sessionId) return;

    let timer = null;

    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      sendBeaconJSON('/api/sessions/heartbeat', { session_id: sessionId });
    };

    const start = () => {
      stop();
      // immediate beat so short sessions still register a "touch"
      tick();
      timer = setInterval(tick, HEARTBEAT_MS);
    };

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [sessionId]);

  // ---------------- unload (tab close / browser quit) ----------------
  useEffect(() => {
    if (!sessionId) return;

    const onPageHide = () => {
      if (endedRef.current) return;
      endedRef.current = true;
      sendBeaconJSON('/api/sessions/end', {
        session_id: sessionId,
        reason: 'unload',
      });
    };

    // `pagehide` is the modern reliable signal (covers bfcache + close).
    // `beforeunload` is a safety net for older browsers / quirky cases.
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
    };
  }, [sessionId]);

  // ---------------- public helpers ----------------
  const logEvent = useCallback(
    (eventType, payload) => {
      const sid = sessionIdRef.current;
      if (!sid || !user) return;
      apiPost(
        '/api/events',
        { session_id: sid, event_type: eventType, payload: payload || null },
        user
      ).catch((err) => {
        if (DEV) console.warn('[session] event failed:', err);
      });
    },
    [user]
  );

  const endSession = useCallback((reason = 'logout') => {
    const sid = sessionIdRef.current;
    if (!sid || endedRef.current) return;
    endedRef.current = true;
    sendBeaconJSON('/api/sessions/end', { session_id: sid, reason });
  }, []);

  return { sessionId, logEvent, endSession };
}
